"use client";

import React from "react";
import type { CSSProperties } from "react";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css/bundle";
import "swiper/css";
import "swiper/css/pagination";

import * as Icon from "@phosphor-icons/react/dist/ssr";

import { getSliderImageCacheBuster } from "../../utils/imageCacheBuster";
import { useSliderImages } from "../../hooks/useSliderImages";

const HERO_AUTOPLAY_MS = 4000;

function KissariaHeroAutoplayStripe({
  cycleKey,
  durationMs,
}: {
  cycleKey: number;
  durationMs: number;
}) {
  return (
    <div
      className="kissaria-hero-autoplay-stripe"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={100}
      aria-label="Temps avant la diapositive suivante"
    >
      <div
        key={cycleKey}
        className="kissaria-hero-autoplay-stripe__fill"
        style={{ animationDuration: `${durationMs}ms` }}
      />
    </div>
  );
}

const SliderFurniture = () => {
  const { slidePaths, isLoading } = useSliderImages();
  const [desktopStripeCycle, setDesktopStripeCycle] = React.useState(0);
  const [mobileStripeCycle, setMobileStripeCycle] = React.useState(0);
  const heroAutoplayEnabled = slidePaths.length > 1;
  const heroAutoplay =
    heroAutoplayEnabled ? { delay: HERO_AUTOPLAY_MS, disableOnInteraction: false } : false;
  const listCategories = [
    {
      id: 1,
      name: "PYJAMAS ET GANDORA",
      icon: require("../../images/kissaria/icon/nightgown.png"),
    },
    {
      id: 2,
      name: "CHALES & FOULARDS",
      icon: require("../../images/kissaria/icon/makeover.png"),
    },
    {
      id: 3,
      name: "LINGERIE ET SOUS-VETEMENTS",
      icon: require("../../images/kissaria/icon/bra.png"),
    },
    {
      id: 4,
      name: "ACCESSOIRES HAMMAM",
      icon: require("../../images/kissaria/icon/hand-sanitizer.png"),
    },
    {
      id: 5,
      name: "BIJOUX",
      icon: require("../../images/kissaria/icon/diamond-ring.png"),
    },
    {
      id: 6,
      name: "LINGE DE MAISON",
      icon: require("../../images/kissaria/icon/kitchen.png"),
    },
    {
      id: 7,
      name: "ACCESSOIRES",
      label: "Accessoires cheveux",
      icon: require("../../images/kissaria/icon/nouveau.png"),
    },
    {
      id: 8,
      name: "DIVERS",
      icon: require("../../images/kissaria/icon/socks.png"),
    },
    {
      id: 9,
      name: "Voir Plus",
      icon: require("../../images/kissaria/icon/plus.png"),
    },
  ];

  return (
    <>
      <div className="mt-3 flex 2xl:justify-end xl:justify-end lg:justify-end max-md:justify-center max-sm:h-[44vh] sm:mt-4">
        <div className="slider-block flex items-start style-two 2xl:w-[98%] xl:w-[98%] lg:w-[98%] max-md:w-full max-md:h-[430px]">
          <div
            className="fx-reveal lg:w-[22%] shrink-0 rounded-b-md 2xl:flex xl:flex lg:flex max-md:hidden flex-col"
            style={{ "--fx-delay": "0ms" } as CSSProperties}
          >
            <div
              style={{ backgroundColor: "#F5F5F5" }}
              className="2xl:py-5 xl:pb-1 lg:py-2"
            >
              {listCategories.map((category, index) => {
                const href = index === listCategories.length - 1
                      ? `/shop`
                  : `/shop?category=${encodeURIComponent(category.name)}`;
                
                return (
                  <a
                    key={category.id}
                    href={href}
                  className={`flex items-center justify-between w-full max-w-full px-2 xl:px-3 py-0.5 xl:py-2.5 2xl:py-3 ${index==0?null:'border-t'}  border-outline mx-auto transition-all duration-200 hover-category-text cursor-pointer`}
                  >
                    <div className="flex items-center p-2 gap-1.5">
                    <span className="font-bold z-10">
                      <img
                        src={category.icon}
                        width={300}
                        height={300}
                        alt="iCON"
                        className="2xl:w-5 2xl:h-5 xl:w-5 xl:h-5 lg:w-4 lg:h-4"
                      />
                    </span>
                    <h4 className="font-bold text-[12px] leading-snug xl:text-[13px] 2xl:text-sm z-10 category-text-col">
                      {"label" in category && category.label
                        ? category.label
                        : category.name}
                    </h4>
                  </div>
                  <div>
                    <Icon.ArrowRight />
                  </div>
                </a>
                );
              })}
            </div>
          </div>
          <div
            className="fx-reveal slider-main mr-2 relative h-[98%] max-sm:h-[58vh] max-sm:p-0 w-full min-w-0 max-md:w-full lg:flex-1 2xl:pl-3 xl:pl-3 lg:pl-3 md:pl-0"
            style={{ "--fx-delay": "120ms" } as CSSProperties}
          >
            {!isLoading && slidePaths.length > 0 && (
              <>
                <div className="max-sm:hidden">
                  <Swiper
                    key="desktop"
                    navigation
                    grabCursor={true}
                    loop={slidePaths.length > 1}
                    autoplay={heroAutoplay}
                    speed={900}
                    slidesPerView={1}
                    spaceBetween={0}
                    className="kissaria-hero-swiper kissaria-hero-swiper--slide"
                    pagination={{ clickable: true, el: "#kissaria-hero-pagination-desktop" }}
                    modules={[Autoplay, Pagination, Navigation]}
                    onSlideChange={() => setDesktopStripeCycle((v) => v + 1)}
                  >
                    {slidePaths.map((path, i) => (
                      <SwiperSlide key={path}>
                        <img
                          src={getSliderImageCacheBuster(path)}
                          width={2560}
                          height={1080}
                          alt={`Slide ${i + 1}`}
                          className="2xl:max-h-[860px] xl:max-h-[736px] max-h-[576px] w-full object-fill max-sm:hidden"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  {heroAutoplayEnabled ? (
                    <KissariaHeroAutoplayStripe
                      cycleKey={desktopStripeCycle}
                      durationMs={HERO_AUTOPLAY_MS}
                    />
                  ) : null}
                  <div
                    id="kissaria-hero-pagination-desktop"
                    className="kissaria-hero-pagination-below"
                  />
                </div>

                <div className="hidden max-sm:block">
                  <Swiper
                    key="mobile"
                    navigation
                    grabCursor={true}
                    loop={slidePaths.length > 1}
                    autoplay={heroAutoplay}
                    speed={900}
                    slidesPerView={1}
                    spaceBetween={0}
                    modules={[Autoplay, Pagination, Navigation]}
                    pagination={{ clickable: true, el: "#kissaria-hero-pagination-mobile" }}
                    className="kissaria-hero-swiper kissaria-hero-swiper--slide max-sm:h-[280px]"
                    onSlideChange={() => setMobileStripeCycle((v) => v + 1)}
                  >
                    {slidePaths.map((path, i) => (
                      <SwiperSlide key={path} className="hidden max-sm:block">
                        <img
                          src={getSliderImageCacheBuster(path)}
                          width={2560}
                          height={1080}
                          alt={`Slide ${i + 1}`}
                          className="w-full h-full object-fill hidden max-sm:block"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  {heroAutoplayEnabled ? (
                    <KissariaHeroAutoplayStripe
                      cycleKey={mobileStripeCycle}
                      durationMs={HERO_AUTOPLAY_MS}
                    />
                  ) : null}
                  <div
                    id="kissaria-hero-pagination-mobile"
                    className="kissaria-hero-pagination-below"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SliderFurniture;

